import { useCallback, useEffect, useRef, useState } from "react";
import { useWebHaptics } from "web-haptics/react";
import { HAPTIC_SUCCESS } from "../hapticPatterns";
import lockBg from "../bg.webp";

type Props = {
  onUnlock: () => void;
};

function formatTime(d: Date) {
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDate(d: Date) {
  return d.toLocaleDateString(undefined, {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

const UNLOCK_MS = 440;

export function LockScreen({ onUnlock }: Props) {
  const now = new Date();
  const { trigger } = useWebHaptics();
  const [unlocking, setUnlocking] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (!unlocking) return;
    const t = window.setTimeout(() => {
      onUnlock();
    }, UNLOCK_MS);
    return () => window.clearTimeout(t);
  }, [unlocking, onUnlock]);

  const handleClick = useCallback(() => {
    if (started.current) return;
    started.current = true;
    void trigger([...HAPTIC_SUCCESS]);
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) {
      onUnlock();
      return;
    }
    setUnlocking(true);
  }, [onUnlock, trigger]);

  return (
    <button
      type="button"
      className={unlocking ? "lock-screen lock-screen--unlocking" : "lock-screen"}
      onClick={handleClick}
      aria-label="Unlock phone"
    >
      <span
        className="lock-screen-bg"
        style={{ backgroundImage: `url(${lockBg})` }}
        aria-hidden
      />
      <span className="lock-screen-scrim" aria-hidden />
      <div className="lock-screen-inner">
        <div className="lock-time">{formatTime(now)}</div>
        <div className="lock-date">{formatDate(now)}</div>
        <div className="lock-hint">Tap to unlock</div>
      </div>
    </button>
  );
}
